import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Alert, AlertTitle, AlertDescription } from '../../components/ui/Alert';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  Eye, 
  Copy, 
  Webhook,
  Settings,
  Shield,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';

const WebhooksManagement = () => {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock webhook data
  const mockWebhooks = [
    {
      id: 1,
      name: 'Ticket Created Webhook',
      url: 'https://api.example.com/webhooks/ticket-created',
      events: ['ticket.created', 'ticket.assigned'],
      status: 'active',
      secret: 'whsec_1234567890abcdef',
      retryCount: 3,
      timeout: 30,
      lastTriggered: '2024-01-15T10:30:00Z',
      successRate: 98.5,
      description: 'Notifies external system when new tickets are created',
      headers: {
        'Content-Type': 'application/json',
        'X-Webhook-Signature': 'sha256'
      }
    },
    {
      id: 2,
      name: 'Agent Status Change',
      url: 'https://slack.example.com/webhooks/agent-status',
      events: ['agent.online', 'agent.offline', 'agent.busy'],
      status: 'active',
      secret: 'whsec_abcdef1234567890',
      retryCount: 5,
      timeout: 15,
      lastTriggered: '2024-01-15T09:15:00Z',
      successRate: 99.2,
      description: 'Updates Slack channel when agent status changes',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer xoxb-token'
      }
    },
    {
      id: 3,
      name: 'SLA Breach Alert',
      url: 'https://monitoring.example.com/alerts/sla',
      events: ['ticket.sla_breach', 'ticket.escalated'],
      status: 'paused',
      secret: 'whsec_paused123456789',
      retryCount: 2,
      timeout: 10,
      lastTriggered: '2024-01-14T16:45:00Z',
      successRate: 95.8,
      description: 'Sends alerts when SLA is breached',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': 'monitoring-key-123'
      }
    },
    {
      id: 4,
      name: 'Customer Feedback',
      url: 'https://analytics.example.com/feedback',
      events: ['feedback.submitted', 'rating.created'],
      status: 'active',
      secret: 'whsec_feedback123456',
      retryCount: 4,
      timeout: 20,
      lastTriggered: '2024-01-15T11:20:00Z',
      successRate: 97.3,
      description: 'Tracks customer feedback and ratings',
      headers: {
        'Content-Type': 'application/json',
        'X-Analytics-Token': 'analytics-token-456'
      }
    }
  ];

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = () => {
    setLoading(true);
    try {
      setWebhooks(mockWebhooks);
      setLoading(false);
    } catch (err) {
      setError('Failed to load webhooks');
      setLoading(false);
    }
  };

  const handleCreateWebhook = (webhookData) => {
    const newWebhook = {
      id: Date.now(),
      ...webhookData,
      status: 'active',
      lastTriggered: null,
      successRate: 100
    };
    setWebhooks([...webhooks, newWebhook]);
    setSuccess('Webhook created successfully');
    setShowForm(false);
  };

  const handleUpdateWebhook = (webhookData) => {
    setWebhooks(webhooks.map(w => 
      w.id === editingWebhook.id ? { ...w, ...webhookData } : w
    ));
    setSuccess('Webhook updated successfully');
    setEditingWebhook(null);
    setShowForm(false);
  };

  const handleDeleteWebhook = (id) => {
    if (window.confirm('Are you sure you want to delete this webhook?')) {
      setWebhooks(webhooks.filter(w => w.id !== id));
      setSuccess('Webhook deleted successfully');
    }
  };

  const handleToggleStatus = (id) => {
    setWebhooks(webhooks.map(w => 
      w.id === id ? { ...w, status: w.status === 'active' ? 'paused' : 'active' } : w
    ));
  };

  const handleTestWebhook = (id) => {
    setSuccess('Test webhook sent successfully');
  };

  const handleCopySecret = (secret) => {
    navigator.clipboard.writeText(secret);
    setSuccess('Secret copied to clipboard');
  };

  const filteredWebhooks = webhooks.filter(webhook => {
    const matchesSearch = webhook.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         webhook.url.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || webhook.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'paused':
        return <Pause className="w-4 h-4 text-yellow-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'success',
      paused: 'warning',
      error: 'destructive'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getSuccessRateColor = (rate) => {
    if (rate >= 95) return 'text-green-600';
    if (rate >= 85) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Webhooks Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Configure webhooks to integrate with external systems and services
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create Webhook
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="default" className="border-green-200 bg-green-50 text-green-800">
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Success</AlertTitle>
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <Input
                placeholder="Search webhooks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="error">Error</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Webhooks Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredWebhooks.map((webhook) => (
          <Card key={webhook.id} className="hover:shadow-lg transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                    <Webhook className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{webhook.name}</CardTitle>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {webhook.description}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(webhook.status)}
                  {getStatusBadge(webhook.status)}
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* URL */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Webhook URL</label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono text-gray-800 dark:text-gray-200">
                    {webhook.url}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(webhook.url)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Events */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Events</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {webhook.events.map((event, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {event}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Configuration */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Retry Count:</span>
                  <span className="ml-2 font-medium">{webhook.retryCount}</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Timeout:</span>
                  <span className="ml-2 font-medium">{webhook.timeout}s</span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Success Rate:</span>
                  <span className={`ml-2 font-medium ${getSuccessRateColor(webhook.successRate)}`}>
                    {webhook.successRate}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Last Triggered:</span>
                  <span className="ml-2 font-medium">
                    {webhook.lastTriggered ? 
                      new Date(webhook.lastTriggered).toLocaleString() : 
                      'Never'
                    }
                  </span>
                </div>
              </div>

              {/* Secret */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Secret</label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded text-sm font-mono text-gray-800 dark:text-gray-200">
                    {webhook.secret.substring(0, 20)}...
                  </code>
                  <Button size="sm" variant="outline" onClick={() => handleCopySecret(webhook.secret)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Headers */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Headers</label>
                <div className="mt-1 space-y-1">
                  {Object.entries(webhook.headers).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-sm">
                      <span className="font-medium text-gray-600 dark:text-gray-400">{key}:</span>
                      <span className="text-gray-800 dark:text-gray-200">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleTestWebhook(webhook.id)}
                  className="flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Test
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingWebhook(webhook);
                    setShowForm(true);
                  }}
                  className="flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleToggleStatus(webhook.id)}
                  className="flex items-center gap-2"
                >
                  {webhook.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  {webhook.status === 'active' ? 'Pause' : 'Resume'}
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDeleteWebhook(webhook.id)}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredWebhooks.length === 0 && !loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <Webhook className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No webhooks found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your search criteria'
                : 'Create your first webhook to get started'
              }
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <Button onClick={() => setShowForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Webhook
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Webhook Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>
                {editingWebhook ? 'Edit Webhook' : 'Create New Webhook'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <WebhookForm
                webhook={editingWebhook}
                onSubmit={editingWebhook ? handleUpdateWebhook : handleCreateWebhook}
                onCancel={() => {
                  setShowForm(false);
                  setEditingWebhook(null);
                }}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Webhook Form Component
const WebhookForm = ({ webhook, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: webhook?.name || '',
    url: webhook?.url || '',
    events: webhook?.events || [],
    description: webhook?.description || '',
    retryCount: webhook?.retryCount || 3,
    timeout: webhook?.timeout || 30,
    headers: webhook?.headers || {}
  });

  const availableEvents = [
    'ticket.created',
    'ticket.updated',
    'ticket.assigned',
    'ticket.resolved',
    'ticket.closed',
    'agent.online',
    'agent.offline',
    'agent.busy',
    'feedback.submitted',
    'rating.created',
    'ticket.sla_breach',
    'ticket.escalated'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleEventToggle = (event) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  const handleHeaderChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      headers: { ...prev.headers, [key]: value }
    }));
  };

  const removeHeader = (key) => {
    setFormData(prev => {
      const newHeaders = { ...prev.headers };
      delete newHeaders[key];
      return { ...prev, headers: newHeaders };
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Webhook Name
          </label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Enter webhook name"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Webhook URL
          </label>
          <Input
            value={formData.url}
            onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
            placeholder="https://api.example.com/webhook"
            type="url"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Describe what this webhook does"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            rows={3}
          />
        </div>
      </div>

      {/* Events */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Events to Subscribe To
        </label>
        <div className="grid grid-cols-2 gap-2">
          {availableEvents.map((event) => (
            <label key={event} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.events.includes(event)}
                onChange={() => handleEventToggle(event)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{event}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Configuration */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Retry Count
          </label>
          <Input
            type="number"
            value={formData.retryCount}
            onChange={(e) => setFormData(prev => ({ ...prev, retryCount: parseInt(e.target.value) }))}
            min="1"
            max="10"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Timeout (seconds)
          </label>
          <Input
            type="number"
            value={formData.timeout}
            onChange={(e) => setFormData(prev => ({ ...prev, timeout: parseInt(e.target.value) }))}
            min="5"
            max="300"
          />
        </div>
      </div>

      {/* Headers */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Custom Headers
        </label>
        <div className="space-y-2">
          {Object.entries(formData.headers).map(([key, value]) => (
            <div key={key} className="flex gap-2">
              <Input
                value={key}
                onChange={(e) => {
                  const newHeaders = { ...formData.headers };
                  delete newHeaders[key];
                  newHeaders[e.target.value] = value;
                  setFormData(prev => ({ ...prev, headers: newHeaders }));
                }}
                placeholder="Header name"
                className="flex-1"
              />
              <Input
                value={value}
                onChange={(e) => handleHeaderChange(key, e.target.value)}
                placeholder="Header value"
                className="flex-1"
              />
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => removeHeader(key)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleHeaderChange('', '')}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Header
          </Button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="submit" className="flex-1">
          {webhook ? 'Update Webhook' : 'Create Webhook'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default WebhooksManagement;
